import React from "react";
import { useQuery } from "@apollo/client";
import { gql } from "graphql-tag";
import moment from "moment";
import { Routes, Route } from "react-router-dom";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import { ContainerCardLayout } from "components/haven-design";
import InterestProfileSlideout from "pages/dashboard/interests-profile-slideout/components/slideout";
import { Viewer } from "components/auth/types";
import { useFeature } from "components/feature/use-feature";
import { authorizeForRole } from "components/auth/authorize-for-role";
import { Feature, UserRole } from "types/gql-types";
import MessageTable from "./messages-table";

const ERROR_TYPE = "ERROR";
const MESSAGE_ERRORS = gql`
  query messageErrors($type: String) {
    messages(type: $type) {
      id
      insertedAt
      status
      recipientName
      interestUrl
      subject
      emailToAddress
      mobileNumber
      bodyPreview
    }
  }
`;

type Message = {
  id: string;
  insertedAt: string;
  status: string;
  recipientName: string;
  interestUrl: string;
  subject: string;
  emailToAddress: string;
  mobileNumber: string;
  bodyPreview: string;
}

type QueryData = {
  messages: Message[];
}
const formatData = (messages: Message[]) => messages.map((msg) => ({
  createdAt: moment(`${msg.insertedAt}Z`).format("M/D/YY - h:mm A"),
  interestUrl: msg.interestUrl,
  type: msg.emailToAddress ? "Email" : "SMS",
  recipientName: msg.recipientName.trim() || "N/A",
  subject: msg.subject || "N/A",
  statusMessage: msg.status,
  body: msg.bodyPreview || "N/A",
}));

type Props = { viewer: Viewer }
const Messages: React.FC<Props> = ({ viewer }) => {
  const { loading, error, data } = useQuery<QueryData>(MESSAGE_ERRORS, {
    fetchPolicy: "cache-first",
    variables: { type: ERROR_TYPE },
  });
  const slideoutEnabled = useFeature(Feature.INTEREST_PROFILE_SLIDEOUT) as boolean;

  return (
    <ContainerCardLayout title="Failed Messages">
      {error && <ServerError desc={error.message} />}
      {loading && <LoadingSpinner />}
      <div style={{ display: "grid", position: "relative" }}>
        <div style={{ overflowX: "auto" }}>
          {(!error && !loading) && (data ? (
            <MessageTable
              rowData={formatData(data.messages)}
              slideoutEnabled={slideoutEnabled} />) : (
            <ServerError desc="Missing Data" />))}
        </div>
        {/* Slideout routes under messages */}
        {slideoutEnabled && (
          <Routes>
            <Route
              path="interests/:interestId/*"
              element={
                <InterestProfileSlideout
                  viewer={viewer}
                  listId={""}
                  sendFullAppToInterestsEnabled={false}
                />
              }
            />
          </Routes>
        )}
      </div>
    </ContainerCardLayout>
  );
};

export default authorizeForRole([
  UserRole.PROPERTY_MANAGER,
  UserRole.SUPERVISOR,
], Messages);
